import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getInventories, getStock, getCategories, getBrands, recordUsage } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Wrench,
  Loader2,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Search,
  ChevronDown,
  Check,
  Trash2,
  X,
  Plus
} from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ItemThumbnail } from '../components/ImageUploader';
import { toast } from 'sonner';
const Usage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [inventories, setInventories] = useState([]);
  const [stock, setStock] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStock, setLoadingStock] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Session state — locked once first item is added
  const [selectedInventory, setSelectedInventory] = useState('');
  const [jobNumber, setJobNumber] = useState('');
  const [sessionJobNumber, setSessionJobNumber] = useState(''); // locked value
  const [sessionInventory, setSessionInventory] = useState(''); // locked value
  const [lineItems, setLineItems] = useState([]);

  // Filter / search state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSubCategory, setFilterSubCategory] = useState('all');
  const [filterBrand, setFilterBrand] = useState('all');
  const [catOpen, setCatOpen] = useState(false);
  const [subCatOpen, setSubCatOpen] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);

  const sessionActive = lineItems.length > 0;

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const [invRes, catRes, brandRes] = await Promise.all([
          getInventories(), getCategories(), getBrands()
        ]);
        setInventories(invRes.data);
        setCategories(catRes.data);
        setBrands(brandRes.data);

        if (user?.role === 'technician' && user?.assigned_inventory_id) {
          setSelectedInventory(user.assigned_inventory_id);
        } else if (invRes.data.length > 0) {
          setSelectedInventory(invRes.data[0].id);
        }
      } catch (err) {
        toast.error('Failed to load initial data');
      } finally {
        setLoading(false);
      }
    };
    fetchInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (selectedInventory) fetchStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInventory]);

  // Reset subcategory when category changes
  useEffect(() => { setFilterSubCategory('all'); }, [filterCategory]);

  const fetchStock = async () => {
    setLoadingStock(true);
    try {
      const stockRes = await getStock({ inventory_id: selectedInventory });
      setStock(stockRes.data);
    } catch (err) {
      toast.error('Failed to load stock');
    } finally {
      setLoadingStock(false);
    }
  };

  const parentCategories = useMemo(() => categories.filter(c => !c.parent_id), [categories]);
  const getSubcategories = (parentId) => categories.filter(c => c.parent_id === parentId);
  const filterSubcategories = useMemo(
    () => filterCategory !== 'all' ? getSubcategories(filterCategory) : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterCategory, categories]
  );

  const filteredStock = useMemo(() => stock.filter(item => {
    if (filterCategory !== 'all') {
      const childIds = getSubcategories(filterCategory).map(c => c.id);
      if (!childIds.includes(item.category_id)) return false;
    }
    if (filterSubCategory !== 'all' && item.category_id !== filterSubCategory) return false;
    if (filterBrand !== 'all' && item.brand_id !== filterBrand) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return item.item_name.toLowerCase().includes(q) ||
      item.internal_code.toLowerCase().includes(q) ||
      (item.brand_name || '').toLowerCase().includes(q) ||
      (item.category_name || '').toLowerCase().includes(q) ||
      (item.vendor_skus || []).some(sku => sku.toLowerCase().includes(q));
  }), [stock, filterCategory, filterSubCategory, filterBrand, searchQuery, categories]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearFilters = () => {
    setFilterCategory('all');
    setFilterSubCategory('all');
    setFilterBrand('all');
    setSearchQuery('');
  };

  const handleJobNumberChange = (val) => {
    if (sessionActive && val !== sessionJobNumber) {
      toast.warning(`Job number is locked to "${sessionJobNumber}" for this session. Cancel the session to change it.`);
      setJobNumber(sessionJobNumber);
      return;
    }
    setJobNumber(val);
  };

  const handleInventoryChange = (val) => {
    if (sessionActive && val !== sessionInventory) {
      toast.warning('Inventory is locked for this session. Cancel to change it.');
      setSelectedInventory(sessionInventory);
      return;
    }
    setSelectedInventory(val);
  };

  const addItem = (stockItem) => {
    if (!jobNumber.trim()) {
      setError('Enter a Job Number before adding items');
      toast.error('Please enter a Job Number first');
      return;
    }
    if (stockItem.quantity <= 0) {
      toast.error('No stock available for this item');
      return;
    }
    const existing = lineItems.find(li => li.item_id === stockItem.item_id);
    if (existing) {
      if (existing.quantity + 1 > stockItem.quantity) {
        toast.warning(`Only ${stockItem.quantity} available.`);
        return;
      }
      setLineItems(prev => prev.map(li =>
        li.item_id === stockItem.item_id ? { ...li, quantity: li.quantity + 1 } : li
      ));
    } else {
      if (!sessionActive) {
        // Lock the session
        setSessionJobNumber(jobNumber.trim());
        setSessionInventory(selectedInventory);
      }
      setLineItems(prev => [...prev, {
        item_id: stockItem.item_id,
        item_name: stockItem.item_name,
        internal_code: stockItem.internal_code,
        image_ids: stockItem.image_ids || [],
        quantity: 1,
        available: stockItem.quantity,
        unit: stockItem.unit
      }]);
    }
    setError('');
  };

  const updateQuantity = (itemId, qty) => {
    const n = Math.max(1, parseInt(qty) || 1);
    setLineItems(prev => prev.map(li => {
      if (li.item_id === itemId) {
        if (n > li.available) {
          toast.warning(`Only ${li.available} ${li.unit} available.`);
          return { ...li, quantity: li.available };
        }
        return { ...li, quantity: n };
      }
      return li;
    }));
  };

  const removeLineItem = (itemId) => {
    setLineItems(prev => {
      const updated = prev.filter(li => li.item_id !== itemId);
      if (updated.length === 0) {
        // Unlock session when empty
        setSessionJobNumber('');
        setSessionInventory('');
      }
      return updated;
    });
  };

  const cancelSession = () => {
    toast.dismiss();
    setLineItems([]);
    setSessionJobNumber('');
    setSessionInventory('');
    setJobNumber('');
    setError('');
    setTimeout(() => toast.info('Session cancelled'), 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!sessionJobNumber || lineItems.length === 0) {
      setError('Add at least one item with a Job Number');
      return;
    }
    setSubmitting(true);
    setError('');
    let okCount = 0;
    const failures = [];
    for (const li of lineItems) {
      try {
        await recordUsage({
          inventory_id: sessionInventory,
          item_id: li.item_id,
          quantity: li.quantity,
          job_number: sessionJobNumber
        });
        okCount += 1;
      } catch (err) {
        failures.push({ name: li.item_name, reason: err.response?.data?.detail || 'failed' });
      }
    }
    setSubmitting(false);
    if (failures.length === 0) {
      toast.success(`Recorded usage for ${okCount} item(s) on ${sessionJobNumber}`);
      cancelSession();
      fetchStock();
    } else {
      toast.error(`${okCount} recorded, ${failures.length} failed`);
      setError(failures.map(f => `${f.name}: ${f.reason}`).join(' | '));
      fetchStock();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-[#0776BD]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="usage-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="hover:bg-[#E6F2FA]" data-testid="back-btn">
          <ArrowLeft size={20} className="text-[#0776BD]" />
        </Button>
        <div className="flex-1">
          <h1 className="heading-2 text-[#1F2937]">Record Usage</h1>
          <p className="text-[#6B7280]">Log material usage for a job — add multiple items before submitting</p>
        </div>
        {sessionActive && (
          <Button
            type="button"
            variant="outline"
            onClick={cancelSession}
            className="relative z-[60] h-10 rounded-lg text-red-600 border-red-200 hover:bg-red-50"
            data-testid="cancel-session-btn"
          >
            <X size={16} className="mr-1" /> Cancel Session
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="destructive" className="rounded-lg">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Top bar: Job# + Inventory (locked once session starts) */}
      <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-[#1F2937] mb-1.5 block">
                Job Number <span className="text-red-500">*</span>
                {sessionActive && <span className="ml-2 text-xs text-[#0776BD]">(locked during session)</span>}
              </Label>
              <Input
                value={jobNumber}
                onChange={(e) => handleJobNumberChange(e.target.value)}
                placeholder="e.g., JOB-2026-001"
                className="h-11 rounded-lg border-gray-300 focus:border-[#0776BD]"
                data-testid="job-number-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#1F2937] mb-1.5 block">
                Inventory <span className="text-red-500">*</span>
                {sessionActive && <span className="ml-2 text-xs text-[#0776BD]">(locked)</span>}
              </Label>
              <Select value={selectedInventory} onValueChange={handleInventoryChange}>
                <SelectTrigger className="h-11 rounded-lg border-gray-300" data-testid="usage-inventory-select">
                  <SelectValue placeholder="Select inventory" />
                </SelectTrigger>
                <SelectContent>
                  {inventories.map(inv => (
                    <SelectItem key={inv.id} value={inv.id}>{inv.inventory_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6">
        {/* Top: Selected items (cart) */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
              <Wrench size={20} className="text-[#0776BD]" />
              Selected Items
              <span className="ml-auto text-sm font-normal text-[#6B7280]">
                {lineItems.length} item{lineItems.length !== 1 ? 's' : ''}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {lineItems.length === 0 ? (
              <div className="py-10 text-center text-[#6B7280] border-2 border-dashed border-gray-300 rounded-lg">
                <Wrench size={28} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No items selected yet</p>
                <p className="text-xs mt-1">Enter a Job # then pick items from the list below</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F6F7]">
                      <TableHead className="font-semibold text-[#1F2937]">Item</TableHead>
                      <TableHead className="font-semibold text-[#1F2937] text-center w-24">Qty</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lineItems.map((li) => (
                      <TableRow key={li.item_id} data-testid={`usage-line-${li.item_id}`}>
                        <TableCell className="py-2">
                          <div className="flex items-center gap-2">
                            <ItemThumbnail imageIds={li.image_ids} size={32} alt={li.item_name} />
                            <div className="min-w-0">
                              <p className="font-medium text-[#1F2937] text-sm truncate">{li.item_name}</p>
                              <p className="text-xs text-[#6B7280] font-mono">{li.internal_code}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="py-2 text-center">
                          <Input
                            type="number"
                            min="1"
                            max={li.available}
                            value={li.quantity}
                            onChange={(e) => updateQuantity(li.item_id, e.target.value)}
                            className="h-8 w-16 mx-auto rounded-md border-gray-300 text-center text-sm"
                            data-testid={`usage-qty-${li.item_id}`}
                          />
                          <p className="text-[10px] text-[#6B7280] mt-0.5">of {li.available}</p>
                        </TableCell>
                        <TableCell className="py-2 text-right">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeLineItem(li.item_id)}
                            className="h-8 w-8 text-red-500 hover:bg-red-50"
                            data-testid={`remove-usage-line-${li.item_id}`}
                            aria-label="Remove item"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <div className="pt-2 border-t border-gray-200 space-y-2">
                  <p className="text-xs text-[#6B7280]">
                    Job: <span className="font-mono text-[#0776BD]">{sessionJobNumber || jobNumber}</span>
                  </p>
                  <Button
                    type="submit"
                    disabled={submitting || lineItems.length === 0}
                    className="w-full bg-[#0776BD] hover:bg-[#055A92] h-12 rounded-lg"
                    data-testid="submit-usage-btn"
                  >
                    {submitting ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Recording...</>
                    ) : (
                      <><CheckCircle size={18} className="mr-2" /> Record Usage ({lineItems.length})</>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Bottom: Items to Record (picker) */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-[#1F2937]">Items to Record</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Filter row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Popover open={catOpen} onOpenChange={setCatOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-10 justify-between rounded-lg border-gray-300 font-normal text-sm w-full" data-testid="usage-category-filter">
                    <span className="truncate">{filterCategory !== 'all' ? parentCategories.find(c => c.id === filterCategory)?.category_name || 'Category' : 'Category'}</span>
                    <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-60" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search categories..." />
                    <CommandList className="max-h-[220px]">
                      <CommandEmpty>No results found</CommandEmpty>
                      <CommandGroup>
                        <CommandItem onSelect={() => { setFilterCategory('all'); setCatOpen(false); }}>
                          <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${filterCategory === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                          All Categories
                        </CommandItem>
                        {parentCategories.map(cat => (
                          <CommandItem key={cat.id} value={cat.category_name} onSelect={() => { setFilterCategory(cat.id); setCatOpen(false); }}>
                            <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${filterCategory === cat.id ? 'opacity-100' : 'opacity-0'}`} />
                            {cat.category_name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              <Popover open={subCatOpen} onOpenChange={setSubCatOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" disabled={filterCategory === 'all'} className="h-10 justify-between rounded-lg border-gray-300 font-normal text-sm w-full" data-testid="usage-subcategory-filter">
                    <span className="truncate">{filterSubCategory !== 'all' ? filterSubcategories.find(c => c.id === filterSubCategory)?.category_name || 'Subcategory' : 'Subcategory'}</span>
                    <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-60" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search subcategories..." />
                    <CommandList className="max-h-[220px]">
                      <CommandEmpty>No results found</CommandEmpty>
                      <CommandGroup>
                        <CommandItem onSelect={() => { setFilterSubCategory('all'); setSubCatOpen(false); }}>
                          <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${filterSubCategory === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                          All Subcategories
                        </CommandItem>
                        {filterSubcategories.map(cat => (
                          <CommandItem key={cat.id} value={cat.category_name} onSelect={() => { setFilterSubCategory(cat.id); setSubCatOpen(false); }}>
                            <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${filterSubCategory === cat.id ? 'opacity-100' : 'opacity-0'}`} />
                            {cat.category_name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              <Popover open={brandOpen} onOpenChange={setBrandOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-10 justify-between rounded-lg border-gray-300 font-normal text-sm w-full" data-testid="usage-brand-filter">
                    <span className="truncate">{filterBrand !== 'all' ? brands.find(b => b.id === filterBrand)?.brand_name || 'Brand' : 'Brand'}</span>
                    <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-60" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search brands..." />
                    <CommandList className="max-h-[220px]">
                      <CommandEmpty>No results found</CommandEmpty>
                      <CommandGroup>
                        <CommandItem onSelect={() => { setFilterBrand('all'); setBrandOpen(false); }}>
                          <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${filterBrand === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                          All Brands
                        </CommandItem>
                        {brands.map(b => (
                          <CommandItem key={b.id} value={b.brand_name} onSelect={() => { setFilterBrand(b.id); setBrandOpen(false); }}>
                            <Check className={`mr-2 h-4 w-4 text-[#0776BD] ${filterBrand === b.id ? 'opacity-100' : 'opacity-0'}`} />
                            {b.brand_name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Search row */}
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" size={18} />
                <Input
                  placeholder="Search by item name, code, or vendor SKU..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-10 rounded-lg border-gray-300 focus:border-[#0776BD]"
                  data-testid="usage-search-input"
                />
              </div>
              {(filterCategory !== 'all' || filterSubCategory !== 'all' || filterBrand !== 'all' || searchQuery) && (
                <Button type="button" variant="ghost" size="sm" onClick={clearFilters} className="h-10 text-xs text-[#6B7280] hover:text-red-500" data-testid="usage-clear-filters-btn">
                  <X size={14} className="mr-1" /> Clear filters
                </Button>
              )}
            </div>

            {/* Items list - 1 column */}
            {loadingStock ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[#0776BD]" />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-2 max-h-[480px] overflow-y-auto pr-1">
                {filteredStock.map((item) => {
                  const alreadyAdded = lineItems.find(li => li.item_id === item.item_id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => addItem(item)}
                      disabled={item.quantity === 0}
                      data-testid={`stock-item-${item.id}`}
                      className={`p-3 border rounded-lg text-left transition-all flex items-center gap-3 ${
                        alreadyAdded ? 'border-[#0776BD] bg-[#E6F2FA]' :
                        item.quantity === 0 ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed' :
                        'border-gray-200 hover:border-[#0776BD] hover:bg-[#E6F2FA]/50'
                      }`}
                    >
                      <ItemThumbnail imageIds={item.image_ids} size={44} alt={item.item_name} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#1F2937] truncate text-sm">{item.item_name}</p>
                        <p className="text-xs text-[#6B7280] truncate">
                          {item.brand_name}{item.brand_name && item.category_name ? ' · ' : ''}{item.category_name}
                        </p>
                        <p className="text-xs text-[#0776BD] font-mono">{item.internal_code}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className={`font-bold ${item.is_low_stock ? 'text-[#F0C500]' : 'text-[#1F2937]'}`}>{item.quantity}</p>
                        <p className="text-[10px] text-[#6B7280]">{item.unit}</p>
                        {alreadyAdded && <Plus size={14} className="text-[#0776BD] mt-0.5 ml-auto" />}
                      </div>
                    </button>
                  );
                })}
                {filteredStock.length === 0 && (
                  <div className="py-12 text-center text-[#6B7280] border-2 border-dashed border-gray-300 rounded-lg">
                    No items match your filters
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Usage;
